import React, { useState, useRef, useMemo, useEffect } from 'react'
import { useThemeContext } from '../../context/ThemeContext.jsx'
import * as api from '../../services/api.js'
import AppSelect from '../../components/AppSelect.jsx'

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function imgPrefix(themeName) {
  if (themeName === 'rainy') return 'rainy'
  if (themeName === 'cafe')  return 'cafe'
  if (themeName === 'quest') return 'quest'
  return 'general'
}

function autoEmoji(name = '') {
  const n = name.toLowerCase()
  if (n.includes('rose'))                               return '🌹'
  if (n.includes('orchid'))                             return '🌺'
  if (n.includes('cactus') || n.includes('succulent')) return '🌵'
  if (n.includes('sunflower'))                          return '🌻'
  if (n.includes('lily'))                               return '💐'
  if (n.includes('daisy'))                              return '🌼'
  if (n.includes('tulip'))                              return '🌷'
  if (n.includes('lavender'))                           return '💜'
  if (n.includes('mint') || n.includes('basil') || n.includes('herb')) return '🌿'
  if (n.includes('tomato'))                             return '🍅'
  if (n.includes('pepper'))                             return '🌶️'
  if (n.includes('carrot'))                             return '🥕'
  if (n.includes('lettuce') || n.includes('spinach'))  return '🥬'
  if (n.includes('cucumber'))                           return '🥒'
  if (n.includes('tree') || n.includes('palm') || n.includes('ficus')) return '🌴'
  if (n.includes('pothos') || n.includes('philodendron') || n.includes('vine')) return '🪴'
  return '🌱'
}

// ── Plant care database ─────────────────────────────────────────────────────
const PLANT_CARE_DB = [
  {
    keywords: ['peace lily', 'spathiphyllum'],
    label: 'Peace Lily',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Drooping leaves are a reliable watering cue — wait until it just starts to droop.',
  },
  {
    keywords: ['pothos', 'epipremnum', 'devil\'s ivy', 'golden pothos'],
    label: 'Pothos',
    water: 'Every 7–10 days',
    fertilize: 'Every 2–3 months',
    repot: 'Every 1–2 years',
    tip: 'Nearly indestructible — tolerates low light and irregular watering.',
  },
  {
    keywords: ['monstera', 'swiss cheese', 'split leaf'],
    label: 'Monstera',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 2 years',
    tip: 'Wipe the large leaves with a damp cloth to keep them dust-free and healthy.',
  },
  {
    keywords: ['snake plant', 'sansevieria', 'mother-in-law', 'dracaena trifasciata'],
    label: 'Snake Plant',
    water: 'Every 14–21 days',
    fertilize: 'Every 2–3 months (growing season only)',
    repot: 'Every 2–3 years',
    tip: 'One of the most drought-tolerant houseplants — overwatering is the main risk.',
  },
  {
    keywords: ['spider plant', 'chlorophytum'],
    label: 'Spider Plant',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Brown leaf tips usually mean fluoride in tap water — switch to filtered water.',
  },
  {
    keywords: ['fiddle leaf', 'ficus lyrata'],
    label: 'Fiddle Leaf Fig',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Hates being moved — find its happy spot and leave it there.',
  },
  {
    keywords: ['zz plant', 'zamioculcas', 'zanzibar gem'],
    label: 'ZZ Plant',
    water: 'Every 14–21 days',
    fertilize: 'Every 3–4 months',
    repot: 'Every 2–3 years',
    tip: 'Stores water in its rhizomes — perfect for forgetful waterers.',
  },
  {
    keywords: ['rubber plant', 'ficus elastica', 'rubber tree'],
    label: 'Rubber Plant',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Clean the glossy leaves regularly to help the plant absorb more light.',
  },
  {
    keywords: ['aloe', 'aloe vera'],
    label: 'Aloe Vera',
    water: 'Every 14–21 days',
    fertilize: 'Once in spring only',
    repot: 'Every 2–3 years',
    tip: 'Let the soil dry out completely between waterings — aloe rots easily when overwatered.',
  },
  {
    keywords: ['orchid', 'phalaenopsis', 'moth orchid'],
    label: 'Orchid',
    water: 'Every 7 days (soak & drain method)',
    fertilize: 'Weekly at quarter strength when flowering',
    repot: 'Every 1–2 years after blooming ends',
    tip: 'Never let an orchid sit in water — always drain the pot completely after watering.',
  },
  {
    keywords: ['cactus', 'succulent', 'echeveria', 'haworthia', 'sedum'],
    label: 'Cactus / Succulent',
    water: 'Every 14–21 days (less in winter)',
    fertilize: 'Monthly (spring–summer only)',
    repot: 'Every 2–3 years',
    tip: 'Use a gritty, fast-draining cactus mix and always let the soil dry fully before watering.',
  },
  {
    keywords: ['philodendron', 'heartleaf', 'brasil'],
    label: 'Philodendron',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Yellow leaves usually mean overwatering; brown tips mean underwatering or low humidity.',
  },
  {
    keywords: ['calathea', 'prayer plant', 'maranta', 'ctenanthe'],
    label: 'Calathea / Prayer Plant',
    water: 'Every 7 days (keep soil consistently moist)',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Very sensitive to fluoride — use filtered or rainwater and maintain high humidity.',
  },
  {
    keywords: ['dracaena', 'corn plant', 'dragon tree'],
    label: 'Dracaena',
    water: 'Every 10–14 days',
    fertilize: 'Every 2 months (spring–summer)',
    repot: 'Every 2–3 years',
    tip: 'Brown leaf tips indicate fluoride sensitivity — use filtered water if possible.',
  },
  {
    keywords: ['boston fern', 'fern', 'nephrolepis'],
    label: 'Fern',
    water: 'Every 3–5 days (keep moist)',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Ferns love humidity — mist daily or place near a humidifier.',
  },
  {
    keywords: ['bird of paradise', 'strelitzia'],
    label: 'Bird of Paradise',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 2–3 years (prefers to be rootbound)',
    tip: 'Needs bright light to thrive — a south or west-facing window is ideal.',
  },
  {
    keywords: ['jade', 'crassula', 'money plant', 'money tree'],
    label: 'Jade Plant',
    water: 'Every 14 days',
    fertilize: 'Every 3–4 months',
    repot: 'Every 2–3 years',
    tip: 'Jade plants can live for decades — they love to be slightly rootbound.',
  },
  {
    keywords: ['anthurium', 'flamingo flower'],
    label: 'Anthurium',
    water: 'Every 7–10 days',
    fertilize: 'Monthly (spring–summer)',
    repot: 'Every 2 years',
    tip: 'Bright indirect light encourages more blooms — direct sun will scorch the leaves.',
  },
  {
    keywords: ['lavender'],
    label: 'Lavender',
    water: 'Every 10–14 days (drought-tolerant once established)',
    fertilize: 'Once in early spring only',
    repot: 'Every 2–3 years',
    tip: 'Lavender needs excellent drainage and full sun — avoid overwatering at all costs.',
  },
  {
    keywords: ['african violet', 'saintpaulia'],
    label: 'African Violet',
    water: 'Every 7 days (water from below)',
    fertilize: 'Every 2 weeks with specialist feed',
    repot: 'Every 6–12 months into a slightly larger pot',
    tip: 'Water from the bottom only — wet leaves cause ugly spots and rot.',
  },
  // ── Herbs ──────────────────────────────────────────────────────────────────
  {
    keywords: ['basil', 'sweet basil', 'thai basil'],
    label: 'Basil',
    water: 'Every 2–3 days (keep moist)',
    fertilize: 'Every 2–4 weeks with diluted feed',
    repot: 'When rootbound (every few weeks if growing fast)',
    tip: 'Pinch off flower buds immediately — once basil flowers, the leaves lose flavour.',
  },
  {
    keywords: ['mint', 'spearmint', 'peppermint'],
    label: 'Mint',
    water: 'Every 2–3 days',
    fertilize: 'Monthly during growing season',
    repot: 'Every year (it spreads fast — keep it contained)',
    tip: 'Grow mint in its own pot; it will take over a shared planter within a season.',
  },
  {
    keywords: ['rosemary'],
    label: 'Rosemary',
    water: 'Every 7–10 days (let soil dry slightly)',
    fertilize: 'Every 4–6 weeks (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Rosemary loves dry conditions — more plants die from overwatering than drought.',
  },
  {
    keywords: ['thyme'],
    label: 'Thyme',
    water: 'Every 7–10 days (drought-tolerant)',
    fertilize: 'Every 6–8 weeks (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Trim regularly to keep it bushy — woody stems are less flavourful.',
  },
  {
    keywords: ['oregano'],
    label: 'Oregano',
    water: 'Every 7–10 days',
    fertilize: 'Every 4–6 weeks (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Harvest frequently — cutting encourages fresh growth and better flavour.',
  },
  {
    keywords: ['chives'],
    label: 'Chives',
    water: 'Every 4–5 days',
    fertilize: 'Monthly during growing season',
    repot: 'Divide every 2–3 years',
    tip: 'Snip from the top rather than pulling — they regrow quickly when cut this way.',
  },
  {
    keywords: ['parsley', 'flat-leaf', 'curly parsley'],
    label: 'Parsley',
    water: 'Every 3–4 days (keep moist)',
    fertilize: 'Every 2–4 weeks',
    repot: 'Replant annually (it\'s a biennial)',
    tip: 'Parsley has a long taproot — use a deep pot and avoid disturbing the roots.',
  },
  {
    keywords: ['cilantro', 'coriander'],
    label: 'Cilantro / Coriander',
    water: 'Every 2–3 days',
    fertilize: 'Every 3–4 weeks (lightly)',
    repot: 'Best started fresh every 3–4 weeks as it bolts quickly',
    tip: 'Bolt-prone in heat — keep it cool and sow successionally for a continuous harvest.',
  },
  {
    keywords: ['sage'],
    label: 'Sage',
    water: 'Every 7–10 days (drought-tolerant)',
    fertilize: 'Every 4–6 weeks (spring–summer)',
    repot: 'Every 1–2 years',
    tip: 'Harvest before flowering for the best flavour and prune hard in spring.',
  },
  // ── Vegetables ─────────────────────────────────────────────────────────────
  {
    keywords: ['tomato', 'cherry tomato', 'beefsteak', 'plum tomato'],
    label: 'Tomato',
    water: 'Daily or every 2 days (keep consistently moist)',
    fertilize: 'Weekly with high-potassium feed once flowering',
    repot: 'Pot up when rootbound; final pot should be 30–45 cm',
    tip: 'Inconsistent watering causes blossom end rot and splitting — keep moisture even.',
  },
  {
    keywords: ['pepper', 'capsicum', 'bell pepper', 'chilli', 'chili'],
    label: 'Pepper / Chilli',
    water: 'Every 2–3 days',
    fertilize: 'Weekly with balanced feed; switch to high-K once fruiting',
    repot: 'Into a 30 cm+ pot when established',
    tip: 'Peppers love heat — keep them somewhere warm and sunny for the best crop.',
  },
  {
    keywords: ['cucumber'],
    label: 'Cucumber',
    water: 'Daily in warm weather',
    fertilize: 'Weekly with balanced feed',
    repot: 'Needs a large container (30 L+) with a support structure',
    tip: 'Pick cucumbers young and often — leaving them on the plant signals it to stop fruiting.',
  },
  {
    keywords: ['lettuce', 'salad leaves', 'rocket', 'arugula', 'mixed greens'],
    label: 'Lettuce / Salad Leaves',
    water: 'Every 2–3 days (keep moist)',
    fertilize: 'Every 2–3 weeks with nitrogen-rich feed',
    repot: 'Shallow troughs or window boxes work well',
    tip: 'Cut-and-come-again varieties regrow if you leave 2–3 cm of stem when harvesting.',
  },
  {
    keywords: ['spinach', 'chard', 'silverbeet'],
    label: 'Spinach / Chard',
    water: 'Every 2–3 days',
    fertilize: 'Every 2–3 weeks',
    repot: 'Grows well in medium-depth containers',
    tip: 'Spinach bolts in heat — grow in spring and autumn for the best harvests.',
  },
  {
    keywords: ['carrot'],
    label: 'Carrot',
    water: 'Every 3–4 days',
    fertilize: 'Every 3–4 weeks (low nitrogen — promotes leafy growth over roots)',
    repot: 'Needs a deep container (at least 30 cm) for good root development',
    tip: 'Rocky or lumpy soil causes forked carrots — use loose, stone-free compost.',
  },
  {
    keywords: ['courgette', 'zucchini', 'marrow'],
    label: 'Courgette / Zucchini',
    water: 'Daily in warm weather',
    fertilize: 'Weekly once flowering begins',
    repot: 'Needs a very large pot (40 L+)',
    tip: 'Harvest at 10–15 cm for best flavour — they turn woody and bitter if left too long.',
  },
  {
    keywords: ['bean', 'runner bean', 'french bean', 'climbing bean', 'broad bean'],
    label: 'Beans',
    water: 'Every 2–3 days',
    fertilize: 'Every 2–3 weeks (low nitrogen — they fix their own)',
    repot: 'Runner/climbing types need a deep pot and tall supports',
    tip: 'Pick beans regularly once they start producing — it stimulates the plant to make more.',
  },
  {
    keywords: ['pea', 'snow pea', 'sugar snap', 'mangetout'],
    label: 'Peas',
    water: 'Every 2–3 days',
    fertilize: 'Minimal — they fix nitrogen naturally',
    repot: 'Grow in a long trough or large pot with twiggy supports',
    tip: 'Peas prefer cool weather — sow early and again in late summer to avoid the heat.',
  },
  {
    keywords: ['strawberry'],
    label: 'Strawberry',
    water: 'Every 2–3 days',
    fertilize: 'Weekly with high-potassium feed when fruiting',
    repot: 'Replace plants every 3 years; propagate from runners',
    tip: 'Place straw under berries to keep them off the soil and prevent rot.',
  },
]

function findCare(name) {
  if (!name || name.trim().length < 3) return null
  const n = name.toLowerCase().trim()
  return PLANT_CARE_DB.find(entry =>
    entry.keywords.some(kw => n.includes(kw) || kw.includes(n))
  ) || null
}

const LOCATIONS = [
  'Windowsill', 'Living Room', 'Bedroom', 'Kitchen', 'Bathroom',
  'Balcony', 'Garden Bed', 'Greenhouse', 'Office', 'Other',
]

// ── Care suggestion card ────────────────────────────────────────────────────
function CareSuggestion({ care }) {
  if (!care) return null
  return (
    <div style={{
      background: 'var(--bg)', border: '1.5px solid var(--accent)',
      borderRadius: 10, padding: '10px 12px',
      animation: 'fadeIn 0.2s ease',
    }}>
      <div style={{
        fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '0.08em', color: 'var(--accent)', marginBottom: 8,
      }}>
        Care guide for {care.label}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {[
          { icon: '💧', label: 'Water',     value: care.water },
          { icon: '🌱', label: 'Fertilise', value: care.fertilize },
          { icon: '🪴', label: 'Repot',     value: care.repot },
        ].map(({ icon, label, value }) => (
          <div
            key={label}
            style={{
              flex: '1 1 120px', background: 'var(--surf)',
              borderRadius: 8, padding: '7px 9px',
            }}
          >
            <div style={{ fontSize: '1rem', marginBottom: 2 }}>{icon}</div>
            <div style={{
              fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.07em', color: 'var(--stone)', marginBottom: 2,
            }}>
              {label}
            </div>
            <div style={{ fontSize: '0.75rem', color: 'var(--ink)', lineHeight: 1.4 }}>
              {value}
            </div>
          </div>
        ))}
      </div>
      <div style={{
        fontSize: '0.75rem', color: 'var(--bark)', lineHeight: 1.55,
        borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: 7,
      }}>
        💡 {care.tip}
      </div>
    </div>
  )
}

// ── Add plant form ──────────────────────────────────────────────────────────
function AddPlantForm({ defaultType, showTypePicker, onAdd, onCancel }) {
  const [name,     setName]     = useState('')
  const [seeded,   setSeeded]   = useState('')
  const [location, setLocation] = useState('')
  const [type,     setType]     = useState(defaultType)

  const suggestion = useMemo(() => findCare(name), [name])

  function handleSubmit(e) {
    e.preventDefault()
    if (!name.trim()) return
    onAdd({
      id:       'p' + Date.now(),
      name:     name.trim(),
      type,
      emoji:    autoEmoji(name),
      image:    null,
      seeded,
      location,
      notes:    [],
      status:   'active',
      care:     suggestion || null,
    })
  }

  const fieldStyle = {
    background: 'var(--bg)', border: '1px solid var(--stone)',
    borderRadius: 8, padding: '5px 8px', fontSize: '0.8rem',
    fontFamily: 'var(--font-sans)', color: 'var(--ink)', width: '100%',
    boxSizing: 'border-box',
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{
        background: 'var(--surf)', borderRadius: 12, padding: '12px 14px',
        border: '1.5px solid var(--stone)', marginBottom: 10,
        display: 'flex', flexDirection: 'column', gap: 8,
      }}
    >
      <input
        autoFocus
        type="text"
        placeholder={`Plant name (e.g. ${defaultType === 'houseplant' ? 'Peace Lily' : 'Basil'})`}
        value={name}
        onChange={e => setName(e.target.value)}
        style={fieldStyle}
      />

      {/* Live care suggestion */}
      {suggestion && <CareSuggestion care={suggestion} />}

      {showTypePicker && (
        <AppSelect
          value={type}
          onChange={setType}
          options={[{ value: 'herb', label: 'Herb' }, { value: 'vegetable', label: 'Vegetable' }]}
        />
      )}

      <div style={{ display: 'flex', gap: 6 }}>
        <input
          type="date"
          value={seeded}
          onChange={e => setSeeded(e.target.value)}
          style={{ ...fieldStyle, flex: 1 }}
          title="Date seeded"
        />
        <div style={{ flex: 1 }}>
          <AppSelect
            value={location}
            onChange={setLocation}
            options={[{ value: '', label: 'Location…' }, ...LOCATIONS.map(l => ({ value: l, label: l }))]}
            placeholder="Location…"
          />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 6 }}>
        <button
          type="submit"
          style={{
            background: 'var(--accent)', color: 'white', border: 'none',
            borderRadius: 8, padding: '5px 14px', fontSize: '0.78rem',
            fontFamily: 'var(--font-sans)', cursor: 'pointer',
          }}
        >
          Add Plant
        </button>
        <button
          type="button"
          onClick={onCancel}
          style={{
            background: 'transparent', color: 'var(--stone)',
            border: '1px solid var(--stone)', borderRadius: 8,
            padding: '5px 14px', fontSize: '0.78rem',
            fontFamily: 'var(--font-sans)', cursor: 'pointer',
          }}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

// ── Single plant card ───────────────────────────────────────────────────────
function PlantCard({ plant, onUpdate, onDelete }) {
  const [addingNote,    setAddingNote]    = useState(false)
  const [noteText,      setNoteText]      = useState('')
  const [careExpanded,  setCareExpanded]  = useState(false)
  const fileRef = useRef()

  const isArchived = plant.status !== 'active'

  function handlePhoto(e) {
    const file = e.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => onUpdate({ ...plant, image: ev.target.result })
    reader.readAsDataURL(file)
  }

  function saveNote() {
    if (!noteText.trim()) return
    onUpdate({
      ...plant,
      notes: [...plant.notes, { date: todayISO(), text: noteText.trim() }],
    })
    setNoteText('')
    setAddingNote(false)
  }

  function setStatus(status) {
    onUpdate({ ...plant, status })
  }

  const typeBadgeLabel =
    plant.type === 'houseplant' ? 'house'
    : plant.type === 'vegetable' ? 'veg'
    : plant.type

  return (
    <div className={`plant-card-g${isArchived ? ' archived' : ''}`}>
      {/* Photo / emoji area */}
      <div className="plant-card-img-area" onClick={() => fileRef.current?.click()}>
        {plant.image
          ? <img src={plant.image} alt={plant.name} />
          : <span>{plant.emoji || '🌱'}</span>
        }
        <div className="plant-upload-overlay">📷<br />Change photo</div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handlePhoto}
        />
      </div>

      <div className="plant-card-body">
        <div className="plant-card-name">
          {plant.name}
          <span className="plant-type-badge">{typeBadgeLabel}</span>
        </div>

        <div className="plant-card-meta">
          {plant.seeded   && <span>🌱 Seeded {plant.seeded}</span>}
          {plant.location && <span>{plant.seeded ? ' · ' : ''}📍 {plant.location}</span>}
        </div>

        {plant.status === 'harvested' && (
          <span className="plant-status-badge harvested">🌾 Harvested</span>
        )}
        {plant.status === 'passed' && (
          <span className="plant-status-badge passed">🍂 Passed Away</span>
        )}

        {/* Inline care schedule — collapsed by default */}
        {plant.care && (
          <div style={{ marginBottom: '0.35rem' }}>
            <button
              onClick={() => setCareExpanded(v => !v)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: '0.68rem', color: 'var(--accent)', padding: 0,
                fontFamily: 'var(--font-sans)', display: 'flex', alignItems: 'center', gap: 3,
              }}
            >
              {careExpanded ? '▲' : '▼'} Care schedule
            </button>
            {careExpanded && (
              <div style={{ marginTop: 5 }}>
                {[
                  { icon: '💧', label: 'Water',     value: plant.care.water },
                  { icon: '🌱', label: 'Fertilise', value: plant.care.fertilize },
                  { icon: '🪴', label: 'Repot',     value: plant.care.repot },
                ].map(({ icon, label, value }) => (
                  <div
                    key={label}
                    style={{
                      display: 'flex', alignItems: 'baseline', gap: 5,
                      fontSize: '0.72rem', color: 'var(--bark)',
                      padding: '2px 0', borderBottom: '1px solid rgba(0,0,0,0.05)',
                    }}
                  >
                    <span>{icon}</span>
                    <span style={{ color: 'var(--stone)', minWidth: 52 }}>{label}</span>
                    <span>{value}</span>
                  </div>
                ))}
                {plant.care.tip && (
                  <div style={{
                    marginTop: 5, fontSize: '0.71rem', color: 'var(--stone)',
                    fontStyle: 'italic', lineHeight: 1.45,
                  }}>
                    💡 {plant.care.tip}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Recent notes */}
        {plant.notes.length > 0 && (
          <div className="plant-notes-list">
            {plant.notes.slice(-3).map((n, i) => (
              <div key={i} className="plant-note-item">
                <span className="plant-note-date">{n.date}</span>
                {n.text}
              </div>
            ))}
          </div>
        )}

        {/* Add-note form */}
        {addingNote && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4 }}>
            <textarea
              autoFocus
              value={noteText}
              onChange={e => setNoteText(e.target.value)}
              placeholder="How's it doing today?"
              style={{
                background: 'var(--bg)', border: '1px solid var(--stone)',
                borderRadius: 6, padding: '4px 6px', fontSize: '0.75rem',
                fontFamily: 'var(--font-sans)', color: 'var(--ink)',
                resize: 'vertical', minHeight: 48, width: '100%', boxSizing: 'border-box',
              }}
            />
            <div style={{ display: 'flex', gap: 4 }}>
              <button className="plant-action-btn" onClick={saveNote}>Save</button>
              <button className="plant-action-btn" onClick={() => { setAddingNote(false); setNoteText('') }}>Cancel</button>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="plant-card-actions">
          {!addingNote && (
            <button className="plant-action-btn" onClick={() => setAddingNote(true)}>+ Note</button>
          )}
          {!isArchived && plant.type !== 'houseplant' && (
            <button
              className="plant-action-btn"
              title="Mark as harvested"
              onClick={() => setStatus('harvested')}
            >
              🌾
            </button>
          )}
          {!isArchived && (
            <button
              className="plant-action-btn"
              title="Mark as passed away"
              onClick={() => setStatus('passed')}
            >
              🍂
            </button>
          )}
          {isArchived && (
            <button className="plant-action-btn" onClick={() => setStatus('active')}>
              ↩ Restore
            </button>
          )}
          <button
            className="plant-action-btn danger"
            title="Delete plant"
            onClick={onDelete}
          >
            🗑️
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Plant section (House Plants or Herbs & Veg) ─────────────────────────────
function PlantSection({ title, mascotSrc, filterFn, defaultType, showTypePicker, plants, onAdd, onUpdate, onDelete }) {
  const [adding,       setAdding]       = useState(false)
  const [showArchived, setShowArchived] = useState(false)

  const sectionPlants = plants.filter(filterFn)
  const active   = sectionPlants.filter(p => p.status === 'active')
  const archived = sectionPlants.filter(p => p.status !== 'active')

  function handleAdd(plant) {
    onAdd(plant)
    setAdding(false)
  }

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      <div className="plant-section-header">
        <img className="plant-section-mascot" src={mascotSrc} alt={title} />
        <div style={{ flex: 1 }}>
          <div style={{
            fontFamily: 'var(--font-serif)', fontSize: '1rem',
            color: 'var(--ink)', marginBottom: 2,
          }}>
            {title}
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>
            {active.length} active{archived.length > 0 ? ` · ${archived.length} archived` : ''}
          </div>
        </div>
        <button
          onClick={() => setAdding(v => !v)}
          style={{
            background: 'var(--surf)', border: '1.5px solid var(--stone)',
            borderRadius: 10, padding: '5px 14px', fontSize: '0.78rem',
            fontFamily: 'var(--font-sans)', color: 'var(--bark)', cursor: 'pointer',
          }}
        >
          + Add Plant
        </button>
      </div>

      {adding && (
        <AddPlantForm
          defaultType={defaultType}
          showTypePicker={showTypePicker}
          onAdd={handleAdd}
          onCancel={() => setAdding(false)}
        />
      )}

      <div className="plant-grid">
        {active.map(p => (
          <PlantCard
            key={p.id}
            plant={p}
            onUpdate={onUpdate}
            onDelete={() => onDelete(p.id)}
          />
        ))}
        {active.length === 0 && !adding && (
          <p style={{
            fontSize: '0.8rem', color: 'var(--stone)', fontStyle: 'italic',
            gridColumn: '1 / -1', paddingTop: '0.5rem',
          }}>
            No {title.toLowerCase()} yet — add your first one!
          </p>
        )}
      </div>

      {archived.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <button
            className="garden-filter-chip"
            onClick={() => setShowArchived(v => !v)}
            style={{ marginBottom: 8 }}
          >
            {showArchived ? '▲' : '▼'} Archived ({archived.length})
          </button>
          {showArchived && (
            <div className="plant-grid">
              {archived.map(p => (
                <PlantCard
                  key={p.id}
                  plant={p}
                  onUpdate={onUpdate}
                  onDelete={() => onDelete(p.id)}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Main PlantDiary component ────────────────────────────────────────────────
export default function PlantDiary() {
  const { themeName } = useThemeContext()
  const pfx = imgPrefix(themeName)

  const [plants, setPlants] = useState([])

  useEffect(() => { api.getGardenPlants().then(d => setPlants(d || [])) }, [])

  function updatePlants(updater) {
    setPlants(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater
      api.saveGardenPlants(next)
      return next
    })
  }

  function addPlant(plant)    { updatePlants(prev => [...prev, plant]) }
  function updatePlant(plant) { updatePlants(prev => prev.map(p => p.id === plant.id ? plant : p)) }
  function deletePlant(id)    { updatePlants(prev => prev.filter(p => p.id !== id)) }

  return (
    <div>
      <PlantSection
        title="House Plants"
        mascotSrc={`/images/${pfx}-full-flower.png`}
        filterFn={p => p.type === 'houseplant'}
        defaultType="houseplant"
        showTypePicker={false}
        plants={plants}
        onAdd={addPlant}
        onUpdate={updatePlant}
        onDelete={deletePlant}
      />

      <PlantSection
        title="Herbs & Vegetables"
        mascotSrc={`/images/${pfx}-basket.png`}
        filterFn={p => p.type === 'herb' || p.type === 'vegetable'}
        defaultType="herb"
        showTypePicker={true}
        plants={plants}
        onAdd={addPlant}
        onUpdate={updatePlant}
        onDelete={deletePlant}
      />
    </div>
  )
}
